import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { clinicsApi } from '../../lib/api';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Mail,
  Calendar,
  Search
} from 'lucide-react';

export const ClinicsPage = () => {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClinic, setEditingClinic] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    contact_email: '',
    is_active: true
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const clinicsData = await clinicsApi.list();
      setClinics(clinicsData.clinics);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreateModal = () => {
    setEditingClinic(null);
    setFormData({
      name: '',
      slug: '',
      contact_email: '',
      is_active: true
    });
    setShowModal(true);
  };

  const openEditModal = (clinic) => {
    setEditingClinic(clinic);
    setFormData({
      name: clinic.name,
      slug: clinic.slug,
      contact_email: clinic.contact_email,
      is_active: clinic.is_active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = { ...formData };

      if (editingClinic) {
        await clinicsApi.update(editingClinic.id, payload);
        toast.success('Klinik güncellendi');
      } else {
        await clinicsApi.create(payload);
        toast.success('Klinik oluşturuldu');
      }
      
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (clinic) => {
    if (!window.confirm(`"${clinic.name}" kliniğini silmek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      await clinicsApi.delete(clinic.id);
      toast.success('Klinik silindi');
      fetchData();
    } catch (error) {
      toast.error(error.message || 'Silme işlemi başarısız');
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const filteredClinics = clinics.filter(clinic => 
    clinic.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    clinic.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
    clinic.contact_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout title="Klinik Yönetimi">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#5A67D8]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Klinik Yönetimi" subtitle="Sistemdeki klinikleri yönetin">
      <div className="space-y-6" data-testid="clinics-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[#5A67D8]" />
            <h2 className="text-xl font-semibold text-slate-800">Klinikler</h2>
            <Badge variant="secondary">{clinics.length} klinik</Badge>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Klinik ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-64"
                data-testid="search-clinics-input"
              />
            </div>
            <Button 
              onClick={openCreateModal}
              className="bg-[#5A67D8] hover:bg-[#4F46E5]"
              data-testid="add-clinic-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Yeni Klinik
            </Button>
          </div>
        </div>

        {/* Clinics Table */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-600">Klinik</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-600">E-posta</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-600">Durum</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-600">Kayıt</th>
                    <th className="text-right px-6 py-4 text-sm font-medium text-slate-600">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredClinics.map((clinic) => (
                    <tr 
                      key={clinic.id} 
                      className="hover:bg-slate-50 transition-colors"
                      data-testid={`clinic-row-${clinic.id}`}
                    >
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-slate-800">{clinic.name}</p>
                          <p className="text-sm text-slate-500">{clinic.slug}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Mail className="w-4 h-4" />
                          {clinic.contact_email}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant={clinic.is_active ? 'default' : 'secondary'}>
                          {clinic.is_active ? 'Aktif' : 'Pasif'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Calendar className="w-4 h-4" />
                          {new Date(clinic.created_at).toLocaleDateString('tr-TR')}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(clinic)}
                            data-testid={`edit-clinic-${clinic.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDelete(clinic)}
                            data-testid={`delete-clinic-${clinic.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Empty State */}
            {filteredClinics.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12">
                <Building2 className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500">
                  {searchTerm ? 'Arama sonucu bulunamadı' : 'Henüz klinik oluşturulmamış'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingClinic ? 'Kliniği Düzenle' : 'Yeni Klinik Oluştur'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Klinik Adı</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setFormData({
                      ...formData, 
                      name,
                      slug: editingClinic ? formData.slug : generateSlug(name)
                    });
                  }}
                  placeholder="Örn: ABC Kadın Doğum Kliniği"
                  required
                  data-testid="clinic-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">Slug (URL)</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({...formData, slug: e.target.value})}
                  placeholder="abc-kadin-dogum-klinigi"
                  required
                  pattern="^[a-z0-9-]+$"
                  data-testid="clinic-slug-input"
                />
                <p className="text-xs text-slate-500">Sadece küçük harf, rakam ve tire kullanılabilir</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact_email">İletişim E-postası</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                  placeholder="iletisim@klinik.com"
                  required
                  data-testid="clinic-email-input"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="w-4 h-4 rounded border-slate-300"
                />
                <Label htmlFor="is_active" className="cursor-pointer">Aktif</Label>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  className="bg-[#5A67D8] hover:bg-[#4F46E5]"
                  disabled={saving}
                  data-testid="save-clinic-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Kaydediliyor...
                    </>
                  ) : (
                    editingClinic ? 'Güncelle' : 'Oluştur'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ClinicsPage;
